/**
 * Format order requirements for display.
 * When the API returns a workflow JSON (nodes/edges), show a human-readable summary
 * instead of raw object string.
 */

interface WorkflowNode {
  id: string
  name?: string
  type?: string
  phase?: string
  survey_id?: string
}

interface WorkflowRequirements {
  nodes?: WorkflowNode[]
  edges?: Array<{ from: string; to: string }>
  start_node?: string
  end_node?: string
  phases?: string[]
}

/**
 * Format requirements for display. If requirements is a JSON workflow object
 * (with nodes/edges), returns a human-readable summary. Otherwise returns
 * the original string.
 */
export function formatOrderRequirements(requirements: string | Record<string, unknown> | null | undefined): string {
  if (requirements == null) return ''

  let trimmed: string
  if (typeof requirements === 'object') {
    try {
      trimmed = JSON.stringify(requirements)
    } catch {
      return ''
    }
  } else {
    trimmed = requirements === '' ? '' : String(requirements).trim()
  }
  if (!trimmed) return ''

  // Workflow JSON: show human-readable summary
  if (trimmed.startsWith('{')) {
    try {
      const data = JSON.parse(trimmed) as WorkflowRequirements
      if (data?.nodes && Array.isArray(data.nodes)) {
        const orderedNames = getWorkflowStepNames(data)
        if (orderedNames.length > 0) {
          return orderedNames.join(' → ')
        }
      }
    } catch {
      // Not valid JSON or not workflow shape: fall through to raw string
    }
  }

  return trimmed
}

function getWorkflowStepNames(data: WorkflowRequirements): string[] {
  const nodes = data.nodes ?? []
  const nodeMap = new Map(nodes.map((n) => [n.id, n]))
  const edges = data.edges ?? []
  const startNode = data.start_node

  if (nodes.length === 0) return []

  // Build order: from start_node follow edges, then append any nodes not in the chain
  const orderedIds: string[] = []
  if (startNode) {
    let current: string | undefined = startNode
    const seen = new Set<string>()
    while (current && !seen.has(current)) {
      seen.add(current)
      orderedIds.push(current)
      const edge = edges.find((e) => e.from === current)
      current = edge?.to
    }
  }

  // If we have no order from edges, use nodes array order or phase order
  if (orderedIds.length === 0 && data.phases?.length) {
    for (const phase of data.phases) {
      const node = nodes.find((n) => n.phase === phase)
      if (node) orderedIds.push(node.id)
    }
  }
  if (orderedIds.length === 0) {
    orderedIds.push(...nodes.map((n) => n.id))
  }

  return orderedIds
    .map((id) => nodeMap.get(id)?.name)
    .filter((name): name is string => Boolean(name))
}
